<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'

// Basic rich-text editor for site pages (About, Terms, …). Wraps TipTap and
// stores plain HTML — the runtime renders `page_content` as-is — with an
// "HTML" toggle that exposes the source for anything the toolbar cannot do.
// The two views share one v-model; switching re-parses the source through
// TipTap so unsupported markup is normalised the way the editor would keep it.
const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	id: {
		type: String,
		default: 'rich-text',
	},
	label: {
		type: String,
		default: 'Content',
	},
	placeholder: {
		type: String,
		default: 'Write your page…',
	},
	hint: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['update:modelValue'])

const EMPTY = '<p></p>'
const normalize = (html) => {
	const v = String(html || '').trim()
	return v === EMPTY ? '' : v
}

const mode = ref('visual') // 'visual' | 'html'
const source = ref(props.modelValue || '')
// Guards against feeding the editor its own output back through the watcher.
let syncing = false

const editor = useEditor({
	content: props.modelValue || '',
	extensions: [
		StarterKit.configure({
			heading: { levels: [1, 2, 3] },
			link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
			codeBlock: false,
		}),
	],
	editorProps: { attributes: { class: 'site-prose', 'aria-label': props.label } },
	onUpdate: ({ editor: ed }) => {
		syncing = true
		const html = normalize(ed.getHTML())
		source.value = html
		emit('update:modelValue', html)
		syncing = false
	},
})

onBeforeUnmount(() => { editor.value?.destroy() })

// External changes (open another page in the same modal) replace the content.
watch(() => props.modelValue, (v) => {
	if (syncing) return
	const next = v || ''
	if (normalize(next) === normalize(source.value)) return
	source.value = next
	editor.value?.commands.setContent(next, { emitUpdate: false })
})

const onSource = (v) => {
	source.value = v
	syncing = true
	emit('update:modelValue', v)
	syncing = false
}

const setMode = (next) => {
	if (next === mode.value) return
	if (next === 'visual' && editor.value) {
		editor.value.commands.setContent(source.value || '', { emitUpdate: false })
		const html = normalize(editor.value.getHTML())
		if (html !== normalize(source.value)) onSource(html)
	}
	mode.value = next
}

const isActive = (name, attrs) => Boolean(editor.value?.isActive(name, attrs))
const run = (fn) => { if (editor.value) fn(editor.value.chain().focus()).run() }
const setLink = () => {
	if (!editor.value) return
	const current = editor.value.getAttributes('link').href || ''
	const url = window.prompt('Link URL (leave empty to remove the link)', current)
	if (url === null) return
	if (!url.trim()) { run((c) => c.extendMarkRange('link').unsetLink()); return }
	run((c) => c.extendMarkRange('link').setLink({ href: url.trim() }))
}

const TOOLBAR = [
	{ key: 'bold', title: 'Bold', label: 'B', cls: 'font-bold', active: () => isActive('bold'), cmd: (c) => c.toggleBold() },
	{ key: 'italic', title: 'Italic', label: 'I', cls: 'italic', active: () => isActive('italic'), cmd: (c) => c.toggleItalic() },
	{ key: 'underline', title: 'Underline', label: 'U', cls: 'underline', active: () => isActive('underline'), cmd: (c) => c.toggleUnderline() },
	{ key: 'h2', title: 'Heading', label: 'H2', active: () => isActive('heading', { level: 2 }), cmd: (c) => c.toggleHeading({ level: 2 }) },
	{ key: 'h3', title: 'Subheading', label: 'H3', active: () => isActive('heading', { level: 3 }), cmd: (c) => c.toggleHeading({ level: 3 }) },
	{ key: 'bullet', title: 'Bulleted list', label: '• List', active: () => isActive('bulletList'), cmd: (c) => c.toggleBulletList() },
	{ key: 'ordered', title: 'Numbered list', label: '1. List', active: () => isActive('orderedList'), cmd: (c) => c.toggleOrderedList() },
	{ key: 'quote', title: 'Quote', label: '❝', active: () => isActive('blockquote'), cmd: (c) => c.toggleBlockquote() },
	{ key: 'rule', title: 'Divider', label: '—', active: () => false, cmd: (c) => c.setHorizontalRule() },
]

const isEmpty = computed(() => !normalize(source.value))
</script>

<template>
	<div class="min-w-0" :data-testid="`${id}-editor`">
		<div class="mb-1.5 flex flex-wrap items-center justify-between gap-2">
			<label class="mb-0 text-tiny font-medium text-surface-text-1" :for="mode === 'html' ? `${id}-html` : undefined">{{ label }}</label>
			<div class="flex items-center gap-1 text-tiny" role="tablist" aria-label="Editor mode">
				<button type="button" role="tab" class="rounded px-2 py-0.5 transition-colors" :aria-selected="mode === 'visual'"
					:class="mode === 'visual' ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white'" @click="setMode('visual')">Visual</button>
				<button type="button" role="tab" class="rounded px-2 py-0.5 font-mono transition-colors" :aria-selected="mode === 'html'"
					:class="mode === 'html' ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white'" data-testid="rich-text-html-toggle" @click="setMode('html')">HTML</button>
			</div>
		</div>

		<div v-show="mode === 'visual'" class="overflow-hidden rounded-lg bg-surface-3 ring-1 ring-surface-4 focus-within:ring-main">
			<div class="flex flex-wrap items-center gap-1 border-b border-surface-4 px-2 py-1.5" role="toolbar" :aria-label="`${label} formatting`">
				<button v-for="tool in TOOLBAR" :key="tool.key" type="button" class="site-rte-tb" :class="[tool.cls, { on: tool.active() }]" :title="tool.title" :aria-label="tool.title" :aria-pressed="tool.active()"
					@mousedown.prevent @click="run(tool.cmd)">{{ tool.label }}</button>
				<button type="button" class="site-rte-tb" :class="{ on: isActive('link') }" title="Link" aria-label="Link" :aria-pressed="isActive('link')" @mousedown.prevent @click="setLink">Link</button>
				<span class="mx-1 h-4 w-px bg-surface-5" />
				<button type="button" class="site-rte-tb" title="Undo" aria-label="Undo" :disabled="!editor?.can().undo()" @mousedown.prevent @click="run((c) => c.undo())">↶</button>
				<button type="button" class="site-rte-tb" title="Redo" aria-label="Redo" :disabled="!editor?.can().redo()" @mousedown.prevent @click="run((c) => c.redo())">↷</button>
			</div>
			<div class="relative px-3 py-2">
				<p v-if="isEmpty" class="pointer-events-none absolute left-3 top-2 mb-0 text-sm text-surface-6" aria-hidden="true">{{ placeholder }}</p>
				<EditorContent :editor="editor" />
			</div>
		</div>

		<FormTextarea v-show="mode === 'html'" :id="`${id}-html`" :model-value="source" :rows="12" :max-rows="30" placeholder="<h1>About us</h1><p>…</p>"
			textarea-classes="!font-[ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] !text-xs !leading-normal" @update:model-value="onSource" />

		<p class="mb-0 mt-1.5 text-tiny text-surface-7">
			{{ mode === 'html' ? 'Raw HTML is saved as-is and rendered on the page. Switch back to Visual to check how it looks.' : (hint || 'Headings, lists, links and quotes. Use HTML for anything else, such as embeds.') }}
		</p>
	</div>
</template>

<style scoped>
.site-rte-tb {
	@apply min-w-[28px] h-7 px-1.5 rounded text-tiny text-surface-8 hover:bg-surface-4 hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent;
}
.site-rte-tb.on {
	@apply bg-surface-4 text-white;
}
</style>

<style>
/* TipTap editing surface for site pages — mirrors the runtime's prose defaults */
.site-prose {
	min-height: 200px;
	outline: none;
	color: var(--c-surface-text-2, #cfd9ee);
	font-size: 14px;
	line-height: 1.6;
}
.site-prose p { margin: 0 0 10px; }
.site-prose a { color: #0062ff; text-decoration: underline; }
.site-prose ul { list-style: disc; padding-left: 20px; margin: 0 0 10px; }
.site-prose ol { list-style: decimal; padding-left: 20px; margin: 0 0 10px; }
.site-prose h1 { font-size: 22px; font-weight: 600; margin: 0 0 10px; }
.site-prose h2 { font-size: 18px; font-weight: 600; margin: 0 0 10px; }
.site-prose h3 { font-size: 15px; font-weight: 600; margin: 0 0 8px; }
.site-prose blockquote { border-left: 3px solid var(--c-dark-5, #3a3f47); padding-left: 12px; margin: 0 0 10px; color: var(--c-dark-8, #9aa3b2); }
.site-prose hr { border: 0; border-top: 1px solid var(--c-dark-5, #3a3f47); margin: 12px 0; }
.site-prose code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; background: rgba(255, 255, 255, 0.06); padding: 1px 4px; border-radius: 3px; }
</style>
